import {useCallback} from "react";
import {ActionIcon, Loader} from "@mantine/core";
import {Navigate, useNavigate} from "react-router-dom";
import IconArrowLeft from "../../assets/arrow-left.svg?react";
import {useExperimentalMode} from "../chat/useExperimentalMode";
import {dibApi} from "../chat/dibApi";
import {useContentParams} from "./router";
import {ChecklistResource} from "./ChecklistResource";
import {ExternalFallback} from "./ExternalFallback";
import {HTMLResource} from "./HTMLResource";
import {ResourceError} from "./ResourceError";
import {StatusMessage} from "./StatusMessage";
import {ResourceType} from "./types";
import layoutClasses from "./ContentLayout.module.css";
import classes from "./ContentPage.module.css";
import {PanelProvider} from "./panel/PanelContext";
import {SliderPanel} from "./panel/SliderPanel";

export const ContentPage = () => {
    const {isExperimental, isLoading: isUserDataLoading} =
        useExperimentalMode();
    const navigate = useNavigate();
    const {
        resourceId,
        dokumentId,
        collectionId,
        segmentId,
        isValidTopicType,
        isPreview,
    } = useContentParams();

    const isSkipped = !resourceId || (!isPreview && !isValidTopicType);
    const {
        data: resourceTypeId,
        isLoading: isPrefetchLoading,
        isError,
        error,
    } = dibApi.endpoints.getResourceTypeId.useQuery(
        {topicId: resourceId},
        {skip: isSkipped}
    );

    const {
        data: dokumentResourceTypeId,
        isLoading: isDokumentPrefetchLoading,
        isError: isDokumentPrefetchError,
        error: dokumentPrefetchError,
    } = dibApi.endpoints.getResourceTypeId.useQuery(
        {topicId: dokumentId},
        {skip: isSkipped || !dokumentId}
    );

    const handleBack = useCallback(() => navigate("/"), [navigate]);

    if (isUserDataLoading) return null;
    if (!isExperimental) return <Navigate to="/" replace />;

    const getContent = () => {
        if (isSkipped) {
            return (
                <StatusMessage
                    title="Content not found"
                    detail="The page you're looking for doesn't exist or the URL is invalid."
                />
            );
        }

        if (isPrefetchLoading) {
            return (
                <div className={layoutClasses.statusMessage}>
                    <Loader color="gneis.5" size="md" />
                </div>
            );
        }

        if (isError) {
            return <ResourceError error={error} />;
        }

        switch (resourceTypeId) {
            case ResourceType.html:
            case ResourceType.staticHtml:
            case ResourceType.skatteAbc:
                return (
                    <HTMLResource
                        resourceId={resourceId!}
                        segmentId={segmentId}
                    />
                );
            case ResourceType.checklist:
                return (
                    <ChecklistResource
                        resourceId={resourceId!}
                        collectionId={collectionId}
                        dokumentId={dokumentId}
                        isPreview={isPreview}
                    />
                );
            default:
                return (
                    <ExternalFallback
                        resourceId={resourceId!}
                        segmentId={segmentId}
                    />
                );
        }
    };

    return (
        <PanelProvider>
            <div className={classes.contentPage}>
                <ActionIcon
                    className={classes.backButton}
                    variant="subtle"
                    color="gneis.8"
                    radius="xl"
                    onClick={handleBack}
                >
                    <IconArrowLeft />
                </ActionIcon>
                {getContent()}
            </div>
            <SliderPanel />
        </PanelProvider>
    );
};
